import React, { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';
import { num, shortAddr, usd } from './format.js';
import { PageShell } from './ui.jsx';

const TABS = [
  ['overview', 'Overview'], ['accounts', 'Accounts'], ['users', 'Users'], ['packs', 'Packs & rules'], ['payouts', 'Payouts'],
  ['commissions', 'Affiliate commissions'], ['coupons', 'Discount codes'], ['flags', 'Abuse flags'], ['payments', 'Payments'], ['settings', 'Settings'],
];
const STAGE = { stage_1: 'Stage 1', stage_2: 'Stage 2', funded: 'Stage 3' };
const when = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function Admin({ user, signOut, toast, go }) {
  const [tab, setTab] = useState('overview');
  return (
    <PageShell user={user} signOut={signOut} go={go} current="admin" wide>
      <p className="eyebrow">Operator</p>
      <h1 className="h2 left">Admin panel</h1>
      <div className="admin-tabs" role="tablist">
        {TABS.map(([id, label]) => <button key={id} role="tab" aria-selected={tab === id} className={'pcat' + (tab === id ? ' on' : '')} onClick={() => setTab(id)}>{label}</button>)}
      </div>
      {tab === 'overview' && <Overview toast={toast} />}
      {tab === 'accounts' && <Accounts toast={toast} />}
      {tab === 'users' && <Users toast={toast} />}
      {tab === 'packs' && <Packs toast={toast} />}
      {tab === 'payouts' && <Payouts toast={toast} />}
      {tab === 'commissions' && <Commissions toast={toast} />}
      {tab === 'coupons' && <Coupons toast={toast} />}
      {tab === 'flags' && <Flags toast={toast} />}
      {tab === 'payments' && <Payments toast={toast} />}
      {tab === 'settings' && <Settings toast={toast} />}
    </PageShell>
  );
}

function useLoad(path, toast, deps = []) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api('GET', path).then(setData).catch((e) => toast(e.message, 'error')), [path, toast, ...deps]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  return [data, load];
}
const Card = ({ title, children, right }) => (
  <section className="card-table">{title && <h2 className="row">{title}<span className="grow" />{right}</h2>}{children}</section>
);
const Empty = ({ children }) => <div className="empty">{children}</div>;

function Overview({ toast }) {
  const [d] = useLoad('/api/admin/overview', toast);
  if (!d) return <div className="spinner" />;
  const tiles = [
    ['Revenue (all time)', usd(d.revenue.total), `${d.revenue.sales} real purchases · ${d.revenue.demoAccounts} demo accounts`],
    ['Revenue (30 days)', usd(d.revenue.last30d), ''],
    ['Traders', num(d.users, 0), `${d.newUsers7d} new this week`],
    ['Active accounts', num(d.accounts.active, 0), `${d.accounts.stage1} stage 1 · ${d.accounts.stage2} stage 2 · ${d.accounts.funded} stage 3`],
    ['Pass rate', `${num(d.passRate, 1)}%`, `${d.accounts.breached} breached of ${d.accounts.total}`],
    ['Rewards requested', usd(d.payouts.owed), `${d.payouts.waiting} waiting for review · ${usd(d.payouts.paid)} paid`],
    ['Affiliate commissions owed', usd(d.affiliates.owed), `${usd(d.affiliates.paid)} paid`],
    ['Abuse flags (30 days)', num(d.flags30d, 0), ''],
  ];
  return (
    <div className="admin-tiles">
      {tiles.map(([l, v, s]) => <div key={l} className="aff-stat tile"><span className="label">{l}</span><span className="mono v">{v}</span>{s && <span className="label">{s}</span>}</div>)}
    </div>
  );
}

function Accounts({ toast }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [rows, load] = useLoad(`/api/admin/accounts?${status ? `status=${status}&` : ''}${q ? `q=${encodeURIComponent(q)}` : ''}`, toast);
  const act = async (id, action) => {
    const reason = action === 'breach' ? prompt('Reason shown to the trader:', 'Closed by admin') : null;
    if (action === 'breach' && reason === null) return;
    try { await api('POST', `/api/admin/accounts/${id}/action`, { action, reason }); toast('Done', 'success'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  return (
    <Card title="Accounts" right={
      <span className="row gap8">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status filter"><option value="">All</option><option value="active">Active</option><option value="breached">Breached</option></select>
        <input className="search" placeholder="Wallet or account id" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search accounts" />
      </span>}>
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>No accounts</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Account</th><th>Trader</th><th>Pack</th><th>Type</th><th>Stage</th><th>Status</th><th>Equity</th><th>Return</th><th>Open</th><th>Days</th><th>Paid</th><th className="r">Action</th></tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.id}>
              <td data-label="Account" className="mono">#{a.id.slice(0, 6).toUpperCase()}</td>
              <td data-label="Trader" className="mono">{shortAddr(a.wallet)}</td>
              <td data-label="Pack">{a.template}</td>
              <td data-label="Type"><span className={'badge ' + (a.isReal ? 'active' : 'demo')}>{a.isReal ? 'Real' : 'Demo'}</span></td>
              <td data-label="Stage">{STAGE[a.stage]}</td>
              <td data-label="Status"><span className={'badge ' + a.status} title={a.breachReason || ''}>{a.status}</span></td>
              <td data-label="Equity" className="mono">{usd(a.equity)}</td>
              <td data-label="Return" className={'mono ' + (a.profitPct > 0 ? 'up' : a.profitPct < 0 ? 'down' : '')}>{num(a.profitPct, 2)}%</td>
              <td data-label="Open" className="mono">{a.openPositions}</td>
              <td data-label="Days" className="mono">{a.tradingDays}</td>
              <td data-label="Paid" className="mono">{usd(a.pricePaid)}</td>
              <td className="r">{a.status === 'active'
                ? <button className="btn sm" onClick={() => act(a.id, 'breach')}>Close</button>
                : <button className="btn sm" onClick={() => act(a.id, 'reactivate')}>Reactivate</button>}</td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

const KYC = { none: 'Not needed', required: 'Required', pending: 'Under review', verified: 'Verified', rejected: 'Rejected' };
function Users({ toast }) {
  const [q, setQ] = useState('');
  const [rows, load] = useLoad(`/api/admin/users?q=${encodeURIComponent(q)}`, toast);
  const setKyc = async (id, kycStatus) => {
    try { await api('PATCH', `/api/admin/users/${id}`, { kycStatus }); toast(`Verification set to: ${KYC[kycStatus]}`, 'success'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  return (
    <Card title="Users" right={<input className="search" placeholder="Wallet or referral code" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search users" />}>
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>No users</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Wallet</th><th>Joined</th><th>Last login</th><th>Accounts</th><th>Stage 3</th><th>Spent (real)</th><th>Terms</th><th>Identity verification</th><th>Referral code</th><th>Referred by</th></tr></thead>
          <tbody>{rows.map((u) => (
            <tr key={u.id}>
              <td data-label="Wallet" className="mono">{u.wallet_address}</td>
              <td data-label="Joined" className="muted">{new Date(u.created_at).toLocaleDateString()}</td>
              <td data-label="Last login" className="muted">{when(u.last_login_at)}</td>
              <td data-label="Accounts" className="mono">{u.accounts}</td>
              <td data-label="Funded" className="mono">{u.funded}</td>
              <td data-label="Spent" className="mono">{usd(u.spent)}</td>
              <td data-label="Terms" className="muted">{u.terms_accepted_at ? new Date(u.terms_accepted_at).toLocaleDateString() : 'Not yet'}</td>
              <td data-label="Verification">
                <select value={u.kyc_status || 'none'} onChange={(e) => setKyc(u.id, e.target.value)} aria-label="Identity verification status">
                  {Object.entries(KYC).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </td>
              <td data-label="Code" className="mono">{u.ref_code || '—'}</td>
              <td data-label="Referred by" className="mono muted">{u.referred_by || '—'}</td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

const PACK_FIELDS = [
  ['name', 'Name', 'text'], ['priceUsd', 'Price $', 'num'], ['startingBalance', 'Account $', 'num'],
  ['stage1ProfitTarget', 'Stage 1 target', 'pct'], ['stage2ProfitTarget', 'Stage 2 target', 'pct'], ['maxDailyLoss', 'Daily loss', 'pct'],
  ['maxTotalDrawdown', 'Max drawdown', 'pct'], ['minTradingDays', 'Min days', 'num'], ['maxLeverage', 'Max lev.', 'num'],
  ['feeRate', 'Fee', 'pct'], ['profitSplit', 'Profit split', 'pct'],
];
function Packs({ toast }) {
  const [rows, load] = useLoad('/api/admin/templates', toast);
  const [edits, setEdits] = useState({});
  const [adding, setAdding] = useState(false);
  // Edits keep exactly what was typed (so "0." and "0.05" can be typed); converted to numbers on save.
  const shown = (t, f, kind) => {
    if (edits[t.id] && f in edits[t.id]) return edits[t.id][f];
    const v = t[f];
    return kind === 'pct' && typeof v === 'number' ? String(Math.round(v * 100 * 1000) / 1000) : String(v ?? '');
  };
  const set = (id, f, kind, raw) => {
    const clean = kind === 'text' ? raw : raw.replace(',', '.').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [f]: clean } }));
  };
  const toBody = (id) => {
    const out = {};
    for (const [f, , kind] of PACK_FIELDS) {
      const e = edits[id] || {};
      if (!(f in e)) continue;
      if (kind === 'text') out[f] = e[f];
      else {
        if (e[f] === '' || !Number.isFinite(Number(e[f]))) throw new Error(`Enter a number for ${PACK_FIELDS.find((x) => x[0] === f)[1]}`);
        out[f] = kind === 'pct' ? Number(e[f]) / 100 : Number(e[f]);
      }
    }
    return out;
  };
  const save = async (id) => {
    try {
      if (id === 'new') await api('POST', '/api/admin/templates', toBody('new'));
      else await api('PATCH', `/api/admin/templates/${id}`, toBody(id));
      toast('Pack saved', 'success'); setEdits((e) => ({ ...e, [id]: undefined })); setAdding(false); load();
    } catch (e) { toast(e.message, 'error'); }
  };
  const toggle = async (t) => { try { await api('PATCH', `/api/admin/templates/${t.id}`, { active: !t.active }); load(); } catch (e) { toast(e.message, 'error'); } };
  const list = rows ? [...rows, ...(adding ? [{ id: 'new', active: true }] : [])] : null;
  return (
    <Card title="Packs & rules" right={<button className="btn sm primary" onClick={() => setAdding(true)} disabled={adding}>+ New pack</button>}>
      <p className="muted small-p pad">Percentages are entered as percent (8 = 8%). Changes apply to new purchases and to the rules of existing accounts on that pack.</p>
      {!list ? <div className="spinner" /> : (
        <div className="table-wrap"><table className="edit-table">
          <thead><tr>{PACK_FIELDS.map(([, l]) => <th key={l}>{l}</th>)}<th>On sale</th><th className="r"></th></tr></thead>
          <tbody>{list.map((t) => (
            <tr key={t.id}>
              {PACK_FIELDS.map(([f, l, kind]) => (
                <td key={f} data-label={l}><input className={kind === 'text' ? 'wide' : ''} aria-label={l} inputMode={kind === 'text' ? undefined : 'decimal'} value={shown(t, f, kind)} onChange={(e) => set(t.id, f, kind, e.target.value)} /></td>
              ))}
              <td data-label="On sale">{t.id !== 'new' && <button className={'badge btnlike ' + (t.active ? 'active' : '')} onClick={() => toggle(t)}>{t.active ? 'Yes' : 'No'}</button>}</td>
              <td className="r"><button className="btn sm primary" onClick={() => save(t.id)} disabled={!edits[t.id]}>Save</button></td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function Payouts({ toast }) {
  const [status, setStatus] = useState('requested');
  const [rows, load] = useLoad(`/api/admin/payouts${status ? `?status=${status}` : ''}`, toast);
  const act = async (id, action) => {
    let txHash = null, note = null;
    if (action === 'paid') { txHash = prompt('Transaction hash of the payment (optional):', ''); if (txHash === null) return; }
    if (action === 'reject') { note = prompt('Reason (shown to the trader):', ''); if (note === null) return; }
    try { await api('POST', `/api/admin/payouts/${id}`, { action, txHash, note }); toast('Payout updated', 'success'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  return (
    <Card title="Stage 3 reward requests" right={<StatusFilter value={status} onChange={setStatus} options={['requested', 'approved', 'paid', 'rejected']} />}>
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>Nothing here</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Requested</th><th>Account</th><th>Pay to wallet</th><th>Profit</th><th>Split</th><th>Amount</th><th>Status</th><th className="r">Action</th></tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.id}>
              <td data-label="Requested" className="muted">{when(p.createdAt)}</td>
              <td data-label="Account" className="mono">#{p.accountId.slice(0, 6).toUpperCase()}</td>
              <td data-label="Wallet" className="mono">{p.wallet}</td>
              <td data-label="Profit" className="mono">{usd(p.grossProfit)}</td>
              <td data-label="Split" className="mono">{Math.round(p.split * 100)}%</td>
              <td data-label="Amount" className="mono up b">{usd(p.amount)}</td>
              <td data-label="Status"><span className="badge">{p.status}</span></td>
              <td className="r nowrap">
                {p.status === 'requested' && <button className="btn sm" onClick={() => act(p.id, 'approve')}>Approve</button>}
                {(p.status === 'requested' || p.status === 'approved') && <button className="btn sm primary" onClick={() => act(p.id, 'paid')}>Mark paid</button>}
                {(p.status === 'requested' || p.status === 'approved') && <button className="btn sm" onClick={() => act(p.id, 'reject')}>Reject</button>}
                {p.txHash && <span className="mono muted small">{p.txHash.slice(0, 10)}…</span>}
              </td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function Commissions({ toast }) {
  const [status, setStatus] = useState('pending');
  const [rows, load] = useLoad(`/api/admin/commissions${status ? `?status=${status}` : ''}`, toast);
  const set = async (id, s) => {
    let txHash = null;
    if (s === 'paid') { txHash = prompt('Transaction hash (optional):', ''); if (txHash === null) return; }
    try { await api('PATCH', `/api/admin/commissions/${id}`, { status: s, txHash }); toast('Commission updated', 'success'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  return (
    <Card title="Affiliate commissions (real purchases only)" right={<StatusFilter value={status} onChange={setStatus} options={['pending', 'approved', 'paid', 'void']} />}>
      <p className="muted small-p pad">Demo and free sign-ups never create commissions. Check the affiliate's identity verification before paying.</p>
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>Nothing here</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Date</th><th>Pay to (affiliate)</th><th>Buyer</th><th>Sale</th><th>Commission</th><th>Status</th><th className="r">Action</th></tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id}>
              <td data-label="Date" className="muted">{when(c.createdAt)}</td>
              <td data-label="Affiliate" className="mono">{c.affiliate}</td>
              <td data-label="Buyer" className="mono">{c.buyer}</td>
              <td data-label="Sale" className="mono">{usd(c.purchaseAmount)}</td>
              <td data-label="Commission" className="mono up b">{usd(c.amount)}</td>
              <td data-label="Status"><span className="badge">{c.status}</span></td>
              <td className="r nowrap">
                {c.status === 'pending' && <button className="btn sm" onClick={() => set(c.id, 'approved')}>Approve</button>}
                {c.status !== 'paid' && c.status !== 'void' && <button className="btn sm primary" onClick={() => set(c.id, 'paid')}>Mark paid</button>}
                {c.status !== 'paid' && c.status !== 'void' && <button className="btn sm" onClick={() => set(c.id, 'void')}>Void</button>}
              </td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function Coupons({ toast }) {
  const [rows, load] = useLoad('/api/admin/coupons', toast);
  const [f, setF] = useState({ code: '', percentOff: '', maxUses: '', expiresAt: '' });
  const create = async () => {
    try {
      await api('POST', '/api/admin/coupons', { code: f.code, percentOff: Number(f.percentOff), maxUses: f.maxUses ? Number(f.maxUses) : null, expiresAt: f.expiresAt || null });
      toast(`Code ${f.code.toUpperCase()} created`, 'success'); setF({ code: '', percentOff: '', maxUses: '', expiresAt: '' }); load();
    } catch (e) { toast(e.message, 'error'); }
  };
  const toggle = async (c) => { try { await api('PATCH', `/api/admin/coupons/${c.id}`, { active: !c.active }); load(); } catch (e) { toast(e.message, 'error'); } };
  return (
    <Card title="Discount codes">
      <div className="form-row pad">
        <input placeholder="CODE" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} aria-label="Code" />
        <input placeholder="% off" inputMode="decimal" value={f.percentOff} onChange={(e) => setF({ ...f, percentOff: e.target.value })} aria-label="Percent off" />
        <input placeholder="Max uses (blank = unlimited)" inputMode="numeric" value={f.maxUses} onChange={(e) => setF({ ...f, maxUses: e.target.value })} aria-label="Max uses" />
        <input type="date" value={f.expiresAt} onChange={(e) => setF({ ...f, expiresAt: e.target.value })} aria-label="Expires" />
        <button className="btn primary" onClick={create}>Create code</button>
      </div>
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>No codes yet</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Code</th><th>Discount</th><th>Used</th><th>Expires</th><th>Status</th></tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id}>
              <td data-label="Code" className="mono b">{c.code}</td>
              <td data-label="Discount" className="mono">{num(c.percentOff, 0)}%</td>
              <td data-label="Used" className="mono">{c.usedCount}{c.maxUses ? ` / ${c.maxUses}` : ''}</td>
              <td data-label="Expires" className="muted">{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : 'Never'}</td>
              <td data-label="Status"><button className={'badge btnlike ' + (c.active ? 'active' : '')} onClick={() => toggle(c)}>{c.active ? 'Active' : 'Off'}</button></td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function Flags({ toast }) {
  const [rows] = useLoad('/api/admin/flags', toast);
  return (
    <Card title="Abuse flags">
      <p className="muted small-p pad">Hedging = the same trader holding opposite positions on one market across their own accounts (one account is then guaranteed to pass). Review before paying out.</p>
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>No flags</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>When</th><th>Type</th><th>Trader</th><th>Account</th><th>Details</th></tr></thead>
          <tbody>{rows.map((f, i) => (
            <tr key={i}>
              <td data-label="When" className="muted">{when(f.created_at)}</td>
              <td data-label="Type" className="down b">{f.kind === 'flag_hedging' ? 'Cross-account hedging' : f.kind}</td>
              <td data-label="Trader" className="mono">{f.wallet_address}</td>
              <td data-label="Account" className="mono">#{f.account_id.slice(0, 6).toUpperCase()}</td>
              <td data-label="Details" className="muted">{f.detail && f.detail.symbol} {f.detail && f.detail.side} vs #{f.detail && f.detail.otherAccount ? f.detail.otherAccount.slice(0, 6).toUpperCase() : ''}</td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function Payments({ toast }) {
  const [rows] = useLoad('/api/admin/payments', toast);
  return (
    <Card title="On-chain payments">
      {!rows ? <div className="spinner" /> : rows.length === 0 ? <Empty>No on-chain payments yet (test mode creates accounts without payments).</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Status</th><th>Amount</th><th>Network</th><th>Transaction</th><th>Error</th></tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.id}>
              <td data-label="Status"><span className={'badge ' + (p.status === 'confirmed' ? 'active' : p.status === 'failed' ? 'breached' : '')}>{p.status}</span></td>
              <td data-label="Amount" className="mono">{usd(p.amountUsd)} {p.token}</td>
              <td data-label="Network">{p.chain}</td>
              <td data-label="Tx">{p.explorer ? <a href={p.explorer} target="_blank" rel="noreferrer" className="mono">{p.txHash.slice(0, 12)}…</a> : '—'}</td>
              <td data-label="Error" className="muted">{p.error || ''}</td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function Settings({ toast }) {
  const [rate, setRate] = useState('');
  const save = async () => {
    try { const r = await api('PATCH', '/api/admin/settings', { affiliateRate: Number(rate) / 100 }); toast(`Affiliate rate set to ${Math.round(r.rate * 100)}%`, 'success'); }
    catch (e) { toast(e.message, 'error'); }
  };
  return (
    <Card title="Settings">
      <div className="form-row pad">
        <label htmlFor="aff-rate" className="label">Affiliate commission %</label>
        <input id="aff-rate" placeholder="20" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
        <button className="btn primary" onClick={save} disabled={!rate}>Save</button>
      </div>
      <BrandSettings toast={toast} />
      <p className="muted small-p pad">Server settings such as payment mode, treasury wallet, admin wallets and risk limits live in the <span className="mono">.env</span> file on the server.</p>
    </Card>
  );
}

const StatusFilter = ({ value, onChange, options }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Status filter">
    <option value="">All</option>{options.map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
  </select>
);

function BrandSettings({ toast }) {
  const [b, setB] = useState('');
  const [o, setO] = useState('');
  const [l, setL] = useState('');
  useEffect(() => { api('GET', '/api/config').then((c) => { setB(c.brandName || ''); setO(c.operatorName || ''); setL(c.logoName || ''); }).catch(() => {}); }, []);
  const save = async () => {
    try { const r = await api('PATCH', '/api/admin/settings', { brandName: b, operatorName: o, logoName: l }); toast(`Saved. Platform name is now ${r.brandName}. Reload to see it everywhere.`, 'success'); }
    catch (e) { toast(e.message, 'error'); }
  };
  return (
    <div className="form-row pad">
      <label htmlFor="brand-name" className="label">Platform name</label>
      <input id="brand-name" value={b} onChange={(e) => setB(e.target.value)} />
      <label htmlFor="logo-name" className="label">Text next to logo</label>
      <input id="logo-name" value={l} placeholder="Empty = platform name" onChange={(e) => setL(e.target.value)} />
      <label htmlFor="op-name" className="label">Operator</label>
      <input id="op-name" value={o} onChange={(e) => setO(e.target.value)} />
      <button className="btn primary" onClick={save}>Save names</button>
    </div>
  );
}
